<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore, useDownloadStore } from '@/stores'
import type { NotificationItem } from '@/types/notification'
import TextTooltip from '@/components/TextTooltip.vue'
import { useTooltip } from '@/utils/useTooltip'
import { formatDateTime } from '@/utils/commonUtil'
import { navOptions, type NavState } from '@/router/navState'

// 칩에 알림 종류를 쓰는 알림. 데이터·랩노트에 대한 알림(DOI 발급 신청·승인·반려, DOI 철회 요청·기각,
// 다운로드 요청 접수·승인·거절, 랩노트 멘션)은 프로젝트·세션을 본문 첫 줄에 따로 보여 준다(랩노트 멘션은 프로젝트만).
// 다운로드 완료·실패 알림은 프로젝트 정보가 없어 칩과 본문만 보인다.
const CHIP_LABELS: Record<string, string> = {
  DOI_REQUEST: 'DOI 발급 신청',
  DOI_APPROVED: 'DOI 발급 승인',
  DOI_REJECTED: 'DOI 발급 반려',
  DOI_RETRACT_REQUEST: 'DOI 철회 요청',
  DOI_RETRACT_REJECTED: 'DOI 철회 기각',
  PARTNER_REQUEST_PENDING: '다운로드 요청 접수',
  PARTNER_REQUEST_APPROVED: '다운로드 요청 승인',
  PARTNER_REQUEST_REJECTED: '다운로드 요청 거절',
  LAB_NOTE_MENTION: 'Lab Note',
  DOWNLOAD_DONE: '다운로드 완료',
  DOWNLOAD_ERROR: '다운로드 실패',
}

// 사유가 붙는 알림 문구 끝의 "(… 사유: 사유)" 부분. 서버 문구 형식:
// "… 신청이 반려되었습니다. (반려 사유: 사유)" / "… 철회 요청이 기각(반려)되었습니다. (사유: 사유)" /
// "… 철회 요청이 접수되었습니다. (신청자: 이름, 사유: 사유)"
const REASON_TYPES = ['DOI_REJECTED', 'DOI_RETRACT_REQUEST', 'DOI_RETRACT_REJECTED']
const REASON_PATTERN = /^([\s\S]*?) \(((?:반려 |신청자: [\s\S]*?, )?사유): ([\s\S]*)\)$/
import {
  Bell, Check, CheckCheck, Trash2, RotateCw,
  BookOpen, Info, ChevronRight, BellOff,
  DownloadCloud, AlertTriangle, ClipboardCheck, ClipboardX
} from 'lucide-vue-next'
import DoiIcon from '@/components/DoiIcon.vue'

// DOI 관련 알림 종류. 아이콘으로 "doi" 글자 아이콘을 쓰고, 배경색은 승인(초록)·반려/기각(빨강)·그 밖(보라)으로 나눈다.
const DOI_TYPES = ['DOI_REQUEST', 'DOI_APPROVED', 'DOI_REJECTED', 'DOI_RETRACT_REQUEST', 'DOI_RETRACT_REJECTED']

// ref, reactive
const router = useRouter()
const userStore = useUserStore()
const downloadStore = useDownloadStore()

const activeTab = ref<'all' | 'unread'>('all')
const isRefreshing = ref<boolean>(false)

// 말줄임으로 잘린 프로젝트명·세션명만 마우스를 올리면 전체 이름을 툴팁으로 보여 준다.
const {
  hoveredTooltipText,
  showTooltip,
  tooltipX,
  tooltipY,
  handleTooltipMouseMove,
  handleTextMouseEnter,
  handleTextMouseLeave,
  handleTooltipMouseEnter,
  handleTooltipMouseLeave
} = useTooltip()

// computed
const filteredNotifications = computed<NotificationItem[]>(() => {
  if (activeTab.value === 'unread') {
    return userStore.notifications.filter((n) => (n.readYn ?? n.isRead) === 'N')
  }
  return userStore.notifications
})

const hasReadNotifications = computed<boolean>(() => {
  return userStore.notifications.some((n) => (n.readYn ?? n.isRead) === 'Y')
})

// 4. method
async function handleRefresh(): Promise<void> {
  isRefreshing.value = true
  try {
    await userStore.fetchNotifications()
  } finally {
    setTimeout(() => {
      isRefreshing.value = false
    }, 400)
  }
}

async function handleMarkAsRead(event: MouseEvent, item: NotificationItem): Promise<void> {
  event.stopPropagation()
  await userStore.markNotificationAsRead(item.id)
}

async function handleDelete(event: MouseEvent, item: NotificationItem): Promise<void> {
  event.stopPropagation()
  await userStore.deleteNotification(item.id)
}

async function handleNotificationClick(item: NotificationItem): Promise<void> {
  if (item.isRead === 'N') {
    await userStore.markNotificationAsRead(item.id)
  }
  userStore.isShowNotifications = false

  // 다운로드 완료/실패 알림은 특정 페이지로 이동할 필요 없이 탑툴바 다운로드 관리
  // 팝업만 열어주면 된다(그 팝업이 이번 세션의 다운로드 목록+이력을 그대로 보여줌).
  if (item.type === 'DOWNLOAD_DONE' || item.type === 'DOWNLOAD_ERROR') {
    downloadStore.openManagerWithHistory()
    return
  }

  if (item.type === 'PARTNER_REQUEST_PENDING') {
    await router.push({ name: 'WORKSPACE', ...navOptions({ tab: 'requests', focus: 'pending-requests' }) })
    return
  }
  if (item.type === 'PARTNER_REQUEST_APPROVED' || item.type === 'PARTNER_REQUEST_REJECTED') {
    await router.push({ name: 'WORKSPACE', ...navOptions({ tab: 'requests', focus: 'my-requests' }) })
    return
  }

  // DOI 신청 접수 알림 (관리자용): 관리자 DOI 관리 탭으로 이동
  if (item.type === 'DOI_REQUEST') {
    await router.push({ path: '/management', ...navOptions({ tab: 'doi', status: 'submitted' }) })
    return
  }

  // DOI 자진 철회 요청 알림 (관리자용): 관리자 DOI 관리 화면의 철회 심사 탭으로 이동
  if (item.type === 'DOI_RETRACT_REQUEST') {
    await router.push({ path: '/management', ...navOptions({ tab: 'doi', status: 'retraction_requested' }) })
    return
  }

  // DOI 승인/반려/철회기각 알림 (신청자 PM용): 해당 데이터 상세 페이지로 이동
  if (item.type === 'DOI_APPROVED' || item.type === 'DOI_REJECTED' || item.type === 'DOI_RETRACT_REJECTED') {
    if (item.referenceId) {
      await router.push({
        name: 'DATA_SHORT',
        params: { dataId: String(item.referenceId) },
        ...navOptions({ tab: 'citation' }),
      })
      return
    }
  }

  if (item.projectId) {
    const state: NavState = { tab: 'labnotes' }
    if (item.referenceId) {
      state.noteId = item.referenceId
    }

    await router.push({
      name: 'PROJECT',
      params: { projectId: String(item.projectId) },
      ...navOptions(state),
    })
  }
}

// 랩노트 멘션 알림은 저장된 문구(예전 "…님이 랩노트 '제목'에서 …" 형식 포함)와 상관없이
// "작성자님이 회원님을 멘션했습니다."로만 보여 준다. 서버 문구는 항상 "작성자님이 "로 시작한다.
const MENTION_AUTHOR_PATTERN = /^([\s\S]+?)님이 /

function displayMessage(item: NotificationItem): string {
  if (item.type === 'LAB_NOTE_MENTION') {
    const author = item.message.match(MENTION_AUTHOR_PATTERN)?.[1]
    if (author) return `${author}님이 회원님을 멘션했습니다.`
  }
  return item.message
}

// 사유가 붙는 알림(반려·철회 요청·기각)은 문구 끝의 사유를 떼어 한 줄로 줄여 보여 준다(잘리면 툴팁으로 전체 표시).
// 그 밖의 알림이거나 형식이 다르면 문구를 그대로 쓴다.
function splitReason(item: NotificationItem): { body: string; label: string; reason: string } {
  const match = REASON_TYPES.includes(item.type) ? item.message.match(REASON_PATTERN) : null
  return match
    ? { body: match[1], label: match[2], reason: match[3] }
    : { body: displayMessage(item), label: '', reason: '' }
}

function formatTime(dateStr: string): string {
  if (!dateStr) return ''
  const isoStr = dateStr.replace(' ', 'T') + (dateStr.includes('T') ? '' : 'Z')
  const dateObj = new Date(isoStr)
  if (isNaN(dateObj.getTime())) return dateStr

  const now = new Date()
  const diffMs = now.getTime() - dateObj.getTime()
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMinutes < 1) return '방금 전'
  if (diffMinutes < 60) return `${diffMinutes}분 전`
  if (diffHours < 24) return `${diffHours}시간 전`
  if (diffDays < 7) return `${diffDays}일 전`

  return formatDateTime(dateObj)
}
</script>

<template>
  <div>
    <!-- Backdrop -->
    <div class="fixed inset-0 z-40" @click="userStore.isShowNotifications = false" />

    <!-- Dropdown Panel -->
    <div class="notification-dropdown-panel shadow-2xl z-50">
      <!-- Header -->
      <div class="notif-header">
        <div class="flex items-center gap-2">
          <div class="notif-bell-icon-badge">
            <Bell :size="16" class="text-indigo-600" />
          </div>
          <span class="notif-title">알림</span>
          <span v-if="userStore.unreadNotificationCount > 0" class="notif-unread-pill">
            {{ userStore.unreadNotificationCount }}건 미확인
          </span>
        </div>

        <div class="flex items-center gap-1">
          <button
            type="button"
            @click="handleRefresh"
            class="notif-icon-btn"
            :class="{ spinning: isRefreshing }"
            v-text-tooltip="'새로고침'"
          >
            <RotateCw :size="14" />
          </button>
          <button
            v-if="userStore.unreadNotificationCount > 0"
            type="button"
            @click="userStore.markAllNotificationsAsRead()"
            class="notif-text-btn"
            v-text-tooltip="'모두 읽음으로 표시'"
          >
            <CheckCheck :size="14" />
            <span>모두 읽음</span>
          </button>
        </div>
      </div>

      <!-- Filter Tabs -->
      <div class="notif-tabs">
        <button
          type="button"
          class="notif-tab"
          :class="{ active: activeTab === 'all' }"
          @click="activeTab = 'all'"
        >
          전체 ({{ userStore.notifications.length }})
        </button>
        <button
          type="button"
          class="notif-tab"
          :class="{ active: activeTab === 'unread' }"
          @click="activeTab = 'unread'"
        >
          읽지 않음 ({{ userStore.unreadNotificationCount }})
        </button>
      </div>

      <!-- Notification List -->
      <div class="notif-list-container custom-scrollbar">
        <div
          v-for="item in filteredNotifications"
          :key="item.id"
          class="notif-item"
          :class="{ unread: item.isRead === 'N' }"
          @click="handleNotificationClick(item)"
        >
          <!-- Left Icon Box -->
          <div
            class="notif-icon-box"
            :class="{
              mention: item.type === 'LAB_NOTE_MENTION' || item.type === 'DOI_REQUEST' || item.type === 'DOI_RETRACT_REQUEST',
              request: item.type === 'PARTNER_REQUEST_PENDING',
              'download-done': item.type === 'DOWNLOAD_DONE' || item.type === 'PARTNER_REQUEST_APPROVED' || item.type === 'DOI_APPROVED',
              'download-error': item.type === 'DOWNLOAD_ERROR' || item.type === 'PARTNER_REQUEST_REJECTED' || item.type === 'DOI_REJECTED' || item.type === 'DOI_RETRACT_REJECTED',
              system: !DOI_TYPES.includes(item.type) && !['LAB_NOTE_MENTION', 'DOWNLOAD_DONE', 'DOWNLOAD_ERROR', 'PARTNER_REQUEST_PENDING', 'PARTNER_REQUEST_APPROVED', 'PARTNER_REQUEST_REJECTED'].includes(item.type),
            }"
          >
            <!-- DOI 알림은 "doi" 글자 아이콘 (승인·반려 등은 배경색과 칩 문구로 구분) -->
            <DoiIcon v-if="DOI_TYPES.includes(item.type)" :size="20" />
            <!-- 랩노트 탭·작성 다이얼로그와 같은 BookOpen 아이콘 -->
            <BookOpen v-else-if="item.type === 'LAB_NOTE_MENTION'" :size="14" />
            <DownloadCloud v-else-if="item.type === 'DOWNLOAD_DONE'" :size="14" />
            <AlertTriangle v-else-if="item.type === 'DOWNLOAD_ERROR'" :size="14" />
            <ClipboardCheck v-else-if="item.type === 'PARTNER_REQUEST_PENDING' || item.type === 'PARTNER_REQUEST_APPROVED'" :size="14" />
            <ClipboardX v-else-if="item.type === 'PARTNER_REQUEST_REJECTED'" :size="14" />
            <Info v-else :size="14" />
          </div>

          <!-- Content Body -->
          <div class="notif-content-area">
            <div class="notif-item-top">
              <span v-if="CHIP_LABELS[item.type]" class="notif-chip">{{ CHIP_LABELS[item.type] }}</span>
              <span v-else-if="item.projectName" class="notif-chip">
                {{ item.projectName }}
              </span>
              <span v-else class="notif-type-tag">알림</span>
              <span class="notif-time">{{ formatTime(item.createDate) }}</span>
            </div>

            <div v-if="CHIP_LABELS[item.type] && (item.projectName || item.sessionName)" class="notif-context">
              <span
                v-if="item.projectName"
                class="notif-context-name"
                @mouseenter="handleTextMouseEnter($event, item.projectName)"
                @mouseleave="handleTextMouseLeave"
                @mousemove="handleTooltipMouseMove"
              >
                {{ item.projectName }}
              </span>
              <span v-if="item.projectName && item.sessionName" class="notif-context-sep">/</span>
              <span
                v-if="item.sessionName"
                class="notif-context-name"
                @mouseenter="handleTextMouseEnter($event, item.sessionName)"
                @mouseleave="handleTextMouseLeave"
                @mousemove="handleTooltipMouseMove"
              >
                {{ item.sessionName }}
              </span>
            </div>

            <p class="notif-message" :class="{ 'has-reason': splitReason(item).reason }">
              {{ splitReason(item).body }}
            </p>
            <div v-if="splitReason(item).reason" class="notif-reason">
              <span class="notif-reason-label">({{ splitReason(item).label }}:</span>
              <span
                class="notif-reason-text"
                @mouseenter="handleTextMouseEnter($event, splitReason(item).reason)"
                @mouseleave="handleTextMouseLeave"
                @mousemove="handleTooltipMouseMove"
              >{{ splitReason(item).reason }}</span>
              <span class="notif-reason-label">)</span>
            </div>
          </div>

          <!-- Unread Dot & Hover Actions -->
          <div class="notif-actions-area">
            <div v-if="item.isRead === 'N'" class="notif-unread-dot" v-text-tooltip="'읽지 않음'"></div>
            <div class="notif-hover-buttons">
              <button
                v-if="item.isRead === 'N'"
                type="button"
                @click="(e) => handleMarkAsRead(e, item)"
                class="notif-action-btn check"
                v-text-tooltip="'읽음으로 표시'"
              >
                <Check :size="12" />
              </button>
              <button
                type="button"
                @click="(e) => handleDelete(e, item)"
                class="notif-action-btn delete"
                v-text-tooltip="'삭제'"
              >
                <Trash2 :size="12" />
              </button>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="filteredNotifications.length === 0" class="notif-empty-state">
          <div class="notif-empty-icon-wrap">
            <BellOff :size="28" class="text-slate-400" />
          </div>
          <p class="notif-empty-title">
            {{ activeTab === 'unread' ? '읽지 않은 알림이 없습니다.' : '수신된 알림이 없습니다.' }}
          </p>
        </div>
      </div>

      <!-- Footer -->
      <div v-if="hasReadNotifications" class="notif-footer">
        <button
          type="button"
          @click="userStore.deleteReadNotifications()"
          class="notif-footer-btn"
        >
          <Trash2 :size="12" />
          <span>읽은 알림 모두 정리</span>
        </button>
      </div>
    </div>

    <TextTooltip
      :show="showTooltip"
      :text="hoveredTooltipText"
      :x="tooltipX"
      :y="tooltipY"
      @mouseenter="handleTooltipMouseEnter"
      @mouseleave="handleTooltipMouseLeave"
    />
  </div>
</template>

<style scoped>
.notification-dropdown-panel {
  position: absolute;
  right: 0;
  top: 52px;
  width: 380px;
  max-width: calc(100vw - 32px);
  background-color: #FFFFFF;
  border-radius: 12px;
  border: 1px solid #E2E8F0;
  box-shadow: 0 20px 30px -10px rgba(15, 23, 42, 0.2), 0 0 1px 1px rgba(15, 23, 42, 0.05);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: notifSlideDown 0.18s cubic-bezier(0.16, 1, 0.3, 1);
  z-index: 50;
}

@keyframes notifSlideDown {
  from {
    opacity: 0;
    transform: translateY(-8px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.notif-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #F1F5F9;
  background-color: #FAFAFA;
}

.notif-bell-icon-badge {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #EEF2FF;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notif-title {
  font-size: 14px;
  font-weight: 700;
  color: #1E293B;
}

.notif-unread-pill {
  font-size: 11px;
  font-weight: 600;
  color: #4F46E5;
  background: #EEF2FF;
  padding: 2px 7px;
  border-radius: 10px;
}

.notif-icon-btn {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid #E2E8F0;
  background: #FFFFFF;
  color: #64748B;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
}

.notif-icon-btn:hover {
  background: #F1F5F9;
  color: #1E293B;
  border-color: #CBD5E1;
}

.notif-icon-btn.spinning svg {
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.notif-text-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 8px;
  border-radius: 6px;
  border: 1px solid #E2E8F0;
  background: #FFFFFF;
  color: #4F46E5;
  font-size: 11.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}

.notif-text-btn:hover {
  background: #EEF2FF;
  border-color: #C7D2FE;
}

.notif-tabs {
  display: flex;
  padding: 4px 12px;
  gap: 6px;
  border-bottom: 1px solid #F1F5F9;
  background: #FFFFFF;
}

.notif-tab {
  flex: 1;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #64748B;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.notif-tab:hover {
  color: #1E293B;
  background: #F8FAFC;
}

.notif-tab.active {
  color: #4F46E5;
  background: #EEF2FF;
}

.notif-list-container {
  max-height: 380px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.notif-item {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #F8FAFC;
  cursor: pointer;
  transition: background-color 0.15s ease;
  background-color: #FFFFFF;
}

.notif-item:hover {
  background-color: #F8FAFC;
}

.notif-item.unread {
  background-color: #F5F7FF;
  border-left: 3px solid #6366F1;
}

.notif-item.unread:hover {
  background-color: #EEF2FF;
}

.notif-icon-box {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
}

.notif-icon-box.mention {
  background: #E0E7FF;
  color: #4F46E5;
}

.notif-icon-box.request {
  background: #EEF2FF;
  color: #4F46E5;
}

.notif-icon-box.system {
  background: #FEF3C7;
  color: #D97706;
}

.notif-icon-box.download-done {
  background: #D1FAE5;
  color: #059669;
}

.notif-icon-box.download-error {
  background: #FEE2E2;
  color: #DC2626;
}

.notif-content-area {
  flex: 1;
  min-width: 0;
}

.notif-item-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 3px;
}

.notif-chip {
  font-size: 11px;
  font-weight: 700;
  color: #4338CA;
  background: #EEF2FF;
  padding: 1px 6px;
  border-radius: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 190px;
}

.notif-type-tag {
  font-size: 11px;
  font-weight: 600;
  color: #64748B;
}

.notif-time {
  font-size: 11px;
  color: #94A3B8;
  white-space: nowrap;
}

/* 데이터에 대한 알림의 "프로젝트명 / 세션명" 줄 */
.notif-context {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  margin-bottom: 2px;
  font-size: 11.5px;
  font-weight: 600;
  color: #475569;
}

/* 이름 하나가 아주 길어도 다른 이름이 보이도록 각각 줄 너비의 65%까지만 쓰고 넘치면 말줄임 */
.notif-context-name {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 65%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.notif-context-name:only-child {
  max-width: 100%;
}

.notif-context-sep {
  flex-shrink: 0;
  color: #CBD5E1;
}

.notif-message {
  font-size: 12.5px;
  line-height: 1.45;
  color: #334155;
  margin: 0 0 4px 0;
  word-break: keep-all;
}

.notif-item.unread .notif-message {
  color: #0F172A;
  font-weight: 500;
}

.notif-message.has-reason {
  margin-bottom: 0;
}

/* 반려·기각 알림의 "(반려 사유: …)", "(사유: …)" 줄: 사유가 길면 한 줄로 줄이고 괄호는 남긴다 */
.notif-reason {
  display: flex;
  min-width: 0;
  margin: 0 0 4px 0;
  font-size: 12.5px;
  line-height: 1.45;
  color: #334155;
}

.notif-item.unread .notif-reason {
  color: #0F172A;
  font-weight: 500;
}

.notif-reason-label {
  flex-shrink: 0;
  white-space: nowrap;
}

.notif-reason-text {
  min-width: 0;
  margin-left: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.notif-actions-area {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  flex-shrink: 0;
}

.notif-unread-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: #6366F1;
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
}

.notif-hover-buttons {
  display: none;
  gap: 3px;
}

.notif-item:hover .notif-hover-buttons {
  display: flex;
}

.notif-action-btn {
  width: 22px;
  height: 22px;
  border-radius: 4px;
  border: 1px solid #CBD5E1;
  background: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.12s ease;
}

.notif-action-btn.check {
  color: #10B981;
}

.notif-action-btn.check:hover {
  background: #ECFDF5;
  border-color: #6EE7B7;
}

.notif-action-btn.delete {
  color: #EF4444;
}

.notif-action-btn.delete:hover {
  background: #FEF2F2;
  border-color: #FCA5A5;
}

.notif-empty-state {
  padding: 36px 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.notif-empty-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #F1F5F9;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
}

.notif-empty-title {
  font-size: 13.5px;
  font-weight: 700;
  color: #334155;
}

.notif-footer {
  padding: 8px 12px;
  border-top: 1px solid #F1F5F9;
  background: #FAFAFA;
  display: flex;
  justify-content: flex-end;
}

.notif-footer-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11.5px;
  color: #64748B;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.15s ease;
}

.notif-footer-btn:hover {
  color: #EF4444;
  background: #FEF2F2;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 5px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #CBD5E1;
  border-radius: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94A3B8;
}
</style>
